/**
 * .tuMetrics {"tu1": 0.84, "tu2": 0.88, "tu3": 4, "notes": "Builds the per-stream Supabase realtime channel: postgres_changes on comments → pure reducer + side effects; client broadcasts (message, reactions, typing); channel health/reconnect; SUBSCRIBED backfill. streamStore owns state and passes get/set in.", "tuVersion": "2.5"}
 * .help "Keeps the room you are looking at live: new, edited and deleted messages appear as they happen, typing dots show up, and if the connection drops it reconnects and refetches."
 *
 * Carved out of streamStore.initializeStreamMessages (Oct 2026, 2.0 Phase D). The store calls
 * attachStreamRealtime() once per active stream and keeps the returned channel in
 * `realtimeChannel`; stopRealtime() tears it down as before.
 *
 * Two behaviours changed on purpose while carving (both were latent bugs):
 *  - UPDATE payloads now get the same content cleaning as INSERTs (agent metadata *and* fenced
 *    action JSON). Before, a reaction on an agent message that carried actions could re-expose
 *    the raw JSON when the payload included `content`.
 *  - The reactions broadcast handler keyed the cache by the *previous* stream (closure over
 *    stale `activeStreamType`). It now reads the active stream from state.
 *  - DELETE never removed anything: with a company_id filter, Realtime drops DELETE events
 *    outright (the key-only old row can't match the filter). A separate unfiltered DELETE
 *    listener now feeds `{ id }` to the reducer, which removes it when it is on screen.
 */
import type { RealtimeChannel } from '@supabase/supabase-js';
import type { ChatSession, Comment, StreamType } from '../../types';
import type { ProposedAction } from '../../utils/agent';
import { supabase } from '../../lib/supabase';
import { mapCommentFromDB } from '../../lib/supabase/queries/comment-queries';
import { fetchLatestStreamMessages } from '../../lib/supabase/queries/stream-queries';
import { extractProposedActionsFromText } from '../../utils/agent';
import useUserStore from '../userStore';
import { relayAgentMessageToPeers } from './agentInvoke';
import { hydrateAgentObjects, stripAgentCreatedObjects } from './agentObjects.ts';
import {
  applyCommentEvent,
  belongsToActive,
  isSystemPayload,
  replaceOptimisticOrAppend,
  sortAsc,
  type CommentEvent,
  type ReducerState,
} from './realtimeReducer.ts';
import { buildStreamKey, isRealtimeChannelUsable, upsertStreamCacheEntry } from './streamCache.ts';

/** How many messages the SUBSCRIBED backfill pulls (newest first). */
export const BACKFILL_LIMIT = 100;

/* ----------------------------------------------------------------------------------------------
 * Content cleaning shared by live INSERT/UPDATE, the backfill, and refreshActiveStream
 * --------------------------------------------------------------------------------------------*/

/** Strip agent-object metadata + fenced action JSON from one message's text. Returns parsed actions. */
export function cleanMessageContent(content: string): { content: string; actions: ProposedAction[] } {
  const stripped = stripAgentCreatedObjects(content || '').content;
  const { cleanText, actions } = extractProposedActionsFromText(stripped);
  return { content: cleanText, actions };
}

/**
 * Clean freshly fetched messages in place and return proposed actions keyed by message id.
 * Fetched messages are never hydrated — their objects already exist in the stores by then.
 */
export function cleanFreshMessages(fresh: Comment[]): Record<string, ProposedAction[]> {
  const actionsMap: Record<string, ProposedAction[]> = {};
  for (const m of fresh) {
    const { content, actions } = cleanMessageContent(m.content || '');
    m.content = content;
    if (actions.length) actionsMap[m.id] = actions;
  }
  return actionsMap;
}

/* ----------------------------------------------------------------------------------------------
 * Host contract — what the store lends to this module
 * --------------------------------------------------------------------------------------------*/

export type RealtimeHostState = ReducerState & {
  chatSessions: ChatSession[];
  realtimeChannel: RealtimeChannel | null;
  isLoading: boolean;
  initializeStreamMessages: (streamType: StreamType, recipientStreamId: string) => Promise<void>;
  updateTypingStatus: (userId: string, timestamp: number) => void;
};

export interface RealtimeHost<S extends RealtimeHostState> {
  get: () => S;
  set: (updater: (s: S) => S | Partial<S>) => void;
  /** A message from this sender just landed: stop any agent typing keep-alive for them. */
  onSenderPosted: (userId: string) => void;
  /** Make sure a chat session row exists for this stream and bump its activity (fire and forget). */
  touchSession: (recipientStreamId: string, streamType: StreamType) => void;
}

export interface AttachStreamRealtimeArgs<S extends RealtimeHostState> {
  host: RealtimeHost<S>;
  topic: string;
  companyId: string;
  me: string;
  streamType: StreamType;
  recipientStreamId: string;
  /** Same stream, channel was torn down: re-subscribe but keep messages and skip the backfill. */
  isResubscribeOnly: boolean;
  log: (...a: unknown[]) => void;
  warn: (...a: unknown[]) => void;
  error: (...a: unknown[]) => void;
}

/* ----------------------------------------------------------------------------------------------
 * attachStreamRealtime
 * --------------------------------------------------------------------------------------------*/

export function attachStreamRealtime<S extends RealtimeHostState>(args: AttachStreamRealtimeArgs<S>): RealtimeChannel {
  const { host, topic, companyId, me, streamType, recipientStreamId, isResubscribeOnly, log, warn, error } = args;

  type CommentRow = Parameters<typeof mapCommentFromDB>[0];
  // Realtime gives `{}` for the absent side (no `new` on DELETE, no `old` on INSERT).
  const asRow = (r: Record<string, unknown>): CommentRow | null =>
    r && Object.keys(r).length ? (r as unknown as CommentRow) : null;

  const channel = supabase
    .channel(topic, { config: { broadcast: { self: true } } })

    // ── Database DELETEs (separate, unfiltered) ────────────────────────────────────────────
    // `comments` has REPLICA IDENTITY DEFAULT, so a DELETE's old row is just `{ id }`. A
    // company_id filter can never match it and the event is silently dropped — which is why
    // deleted messages used to linger until refresh. Subscribing without a filter gets us the
    // id (nothing else leaks); the reducer removes it only if it is on screen. We deliberately
    // do NOT use REPLICA IDENTITY FULL: Realtime skips RLS on DELETE, so that would broadcast
    // full deleted message bodies to every subscriber in the project.
    .on(
      'postgres_changes',
      { event: 'DELETE', schema: 'public', table: 'comments' },
      (payload) => {
        const id = (payload.old as { id?: string } | undefined)?.id;
        if (!id) return;
        host.set((s) => applyCommentEvent(s, { type: 'DELETE', msg: { id } as Comment }, me));
      }
    )

    // ── Database changes (INSERT / UPDATE) ─────────────────────────────────────────────────
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'comments', filter: `company_id=eq.${companyId}` },
      async (payload) => {
        const eventType = payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE';
        if (eventType === 'DELETE') return; // handled above (never actually arrives filtered)
        const rowNew = asRow(payload.new);
        const mappedNew = rowNew ? mapCommentFromDB(rowNew) : null;

        let ev: CommentEvent | null = null;
        if (eventType === 'INSERT' && mappedNew) {
          // Agent-created objects go into their stores BEFORE the message shows, so a card the
          // agent just made is already on the board when its announcement renders.
          const { content, objects } = stripAgentCreatedObjects(mappedNew.content || '');
          if (objects.length) await hydrateAgentObjects(objects);
          const { cleanText, actions } = extractProposedActionsFromText(content);
          mappedNew.content = cleanText;
          ev = { type: 'INSERT', msg: mappedNew, actions };
        } else if (eventType === 'UPDATE' && mappedNew) {
          if (mappedNew.content) mappedNew.content = cleanMessageContent(mappedNew.content).content;
          ev = { type: 'UPDATE', msg: mappedNew };
        }
        if (!ev) return;
        const event = ev;

        const before = host.get();
        const forActive = belongsToActive(event.msg, before.activeStreamType, before.activeRecipientStreamId, me);

        host.set((s) => applyCommentEvent(s, event, me));

        if (event.type !== 'INSERT' || !forActive || isSystemPayload(event.msg)) return;

        // Side effects for a live message in the room on screen.
        host.onSenderPosted(event.msg.userId);
        if (event.msg.recipientStreamId && event.msg.streamType) {
          host.touchSession(event.msg.recipientStreamId, event.msg.streamType);
        }
        // Agent spoke in a meeting → maybe wake the other invited agents. Host-elected inside
        // (decideAgentRelay), so N viewers never mean N× calls.
        relayAgentMessageToPeers({
          arrived: event.msg,
          me,
          allUsers: useUserStore.getState().allUsers,
          getState: () => host.get(),
        });
      }
    )

    // ── Client broadcasts (DB backfill still happens; these are for snappy UX) ─────────────
    .on('broadcast', { event: 'stream_message' }, ({ payload }) => {
      const c = (payload as { comment?: Comment } | undefined)?.comment;
      if (!c) return;
      const meNow = useUserStore.getState().currentUser?.id || me;
      host.set((state) => {
        if (!belongsToActive(c, state.activeStreamType, state.activeRecipientStreamId, meNow)) return state;
        if (state.messages.some((m) => m.id === c.id)) return state;
        const messages = sortAsc(replaceOptimisticOrAppend(state.messages, c));
        const cacheKey = buildStreamKey(state.activeStreamType, state.activeRecipientStreamId);
        return {
          messages,
          messageCacheByStream: cacheKey ? upsertStreamCacheEntry(state, cacheKey, messages) : state.messageCacheByStream,
        } as Partial<S>;
      });
    })
    .on('broadcast', { event: 'stream_message_reactions' }, ({ payload }) => {
      const { messageId, reactions } = (payload as { messageId?: string; reactions?: Record<string, string[]> }) || {};
      if (!messageId || !reactions) return;
      host.set((state) => {
        const idx = state.messages.findIndex((m) => m.id === messageId);
        if (idx === -1) return state;
        const messages = state.messages.map((m, i) => (i === idx ? { ...m, reactions } : m));
        const cacheKey = buildStreamKey(state.activeStreamType, state.activeRecipientStreamId);
        return {
          messages,
          messageCacheByStream: cacheKey ? upsertStreamCacheEntry(state, cacheKey, messages) : state.messageCacheByStream,
        } as Partial<S>;
      });
    })
    .on('broadcast', { event: 'typing_status' }, ({ payload }) => {
      const { userId, isTyping } = payload as { userId: string; isTyping: boolean };
      if (userId === useUserStore.getState().currentUser?.id) return; // never show my own dots
      host.get().updateTypingStatus(userId, isTyping ? Date.now() : 0);
    })

    // ── Health + backfill ──────────────────────────────────────────────────────────────────
    .subscribe(async (status) => {
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
        const latest = host.get();
        const stillActive =
          latest.activeStreamType === streamType && latest.activeRecipientStreamId === recipientStreamId;
        if (stillActive && latest.realtimeChannel === channel) {
          warn('Realtime channel became unhealthy; reconnecting active stream.', { streamType, recipientStreamId, status });
          host.set(() => ({ realtimeChannel: null }) as Partial<S>);
          window.setTimeout(() => {
            const now = host.get();
            const sameStream =
              now.activeStreamType === streamType && now.activeRecipientStreamId === recipientStreamId;
            if (sameStream && !isRealtimeChannelUsable(now.realtimeChannel)) {
              void now.initializeStreamMessages(streamType, recipientStreamId);
            }
          }, 750);
        }
        return;
      }
      if (status !== 'SUBSCRIBED') return;

      if (isResubscribeOnly) {
        log('Re-subscribed; messages already in store, skipping backfill.');
        return;
      }

      try {
        const freshNewestFirst = await fetchLatestStreamMessages(companyId, streamType, recipientStreamId, me, BACKFILL_LIMIT);
        const fresh = [...freshNewestFirst].reverse(); // UI keeps oldest → newest
        const actionsMap = cleanFreshMessages(fresh);
        const hasMore = freshNewestFirst.length === BACKFILL_LIMIT;

        host.set((state) => {
          const byId = new Map<string, Comment>();
          for (const m of state.messages) byId.set(m.id, m); // keep optimistics not yet in DB
          for (const m of fresh) byId.set(m.id, m);
          const merged = sortAsc(Array.from(byId.values()));
          const cacheKey = buildStreamKey(state.activeStreamType, state.activeRecipientStreamId);
          return {
            messages: merged,
            messageCacheByStream: cacheKey
              ? upsertStreamCacheEntry(state, cacheKey, merged, { hasMoreMessages: hasMore, messageOffset: 0 })
              : state.messageCacheByStream,
            isLoading: false,
            hasMoreMessages: hasMore,
            messageOffset: 0, // paging is by time, not offset
            actionsByMessageId: { ...state.actionsByMessageId, ...actionsMap },
          } as Partial<S>;
        });
      } catch (e) {
        error('Backfill fetch failed:', e);
        host.set(() => ({ isLoading: false, hasMoreMessages: false }) as Partial<S>);
      }
    });

  return channel;
}
